<script setup lang="ts">
import {
    computed,
    inject,
    nextTick,
    useTemplateRef,
    type ComputedRef,
} from 'vue'
import { PhCaretDown, PhCheck, PhX } from '@phosphor-icons/vue'
import {
    ComboboxAnchor,
    ComboboxCancel,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxItemIndicator,
    ComboboxRoot,
    ComboboxTrigger,
    ComboboxViewport,
} from 'reka-ui'

interface Option {
    name: string
    value: string | number
}

const props = defineProps<{
    label: string
    name: string
    options: Option[] | string
    value?: Option['value']
}>()

const el = useTemplateRef('el')

// const submit = inject('auto-form:submit', () => {})

const submit = () => {
    const form = el.value?.closest('form')

    if (!form) return

    nextTick(() => {
        // dispatch a SubmitEvent instead of calling .submit() to allow swup to intercept the form
        form.dispatchEvent(new SubmitEvent('submit'))
    })
}

const options: ComputedRef<Option[]> = computed(() => {
    if (typeof props.options === 'string') {
        return JSON.parse(props.options) as Option[]
    }

    return props.options
})

const displayValue = (value: string | number) => {
    if (!options.value || !Array.isArray(options.value)) {
        return ''
    }
    return options.value.find((option) => option.value == value)?.name.toString() ?? ''
}
</script>

<template>
    <div ref="el">
        <ComboboxRoot
            class="relative"
            :name="name"
            :open-on-click="true"
            :reset-search-term-on-select="true"
            :reset-model-value-on-clear="true"
            v-slot="{ modelValue }"
            :default-value="value ?? null"
            @update:model-value="submit"
        >
            <ComboboxAnchor
                class="bg-neutral grid grid-cols-[min-content_32px] rounded"
            >
                <label class="sr-only" :for="name">
                    {{ label }}
                </label>
                <ComboboxInput
                    :id="name"
                    class="field-sizing-content max-w-[288px] py-2 pl-3 text-sm font-medium text-black outline-none placeholder:text-black placeholder:transition-colors focus-within:placeholder:text-stone-400"
                    :placeholder="label"
                    :display-value="displayValue"
                />
                <ComboboxCancel
                    v-if="modelValue"
                    class="flex w-8 cursor-pointer items-center justify-center"
                >
                    <PhX :size="12" />
                </ComboboxCancel>
                <ComboboxTrigger
                    v-else
                    class="group flex w-8 items-center justify-center"
                >
                    <PhCaretDown
                        :size="12"
                        class="group-data-[state=open]:rotate-180"
                    />
                </ComboboxTrigger>
            </ComboboxAnchor>

            <ComboboxContent
                class="data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade border-neutral absolute z-30 mt-1 min-w-full rounded border-2 bg-white shadow-sm will-change-[opacity,transform]"
            >
                <ComboboxViewport
                    class="max-h-[600px] max-w-[320px] overscroll-contain"
                >
                    <ComboboxEmpty
                        class="px-3 py-2 text-center text-xs whitespace-nowrap"
                    >
                        Nichts gefunden.
                    </ComboboxEmpty>

                    <ComboboxItem
                        v-for="option in options"
                        :key="option.name"
                        :value="option.value"
                        class="data-[highlighted]:bg-neutral relative flex cursor-pointer items-center px-3 py-2 pr-[35px] text-sm leading-none whitespace-nowrap select-none first:pt-3 last:pb-3 data-[disabled]:pointer-events-none data-[highlighted]:outline-none"
                    >
                        <ComboboxItemIndicator
                            class="absolute right-2.5 inline-flex items-center justify-center"
                        >
                            <PhCheck />
                        </ComboboxItemIndicator>
                        <span class="truncate">
                            {{ option.name }}
                        </span>
                    </ComboboxItem>
                </ComboboxViewport>
            </ComboboxContent>
        </ComboboxRoot>
    </div>
</template>
